<template>
  <div class="form-wrapper">
    <h2>Passwort vergessen</h2>

    <!-- Schritt 1: E-Mail-Eingabe, um den Code anzufordern -->
    <form v-if="!isCodeStep" @submit.prevent="handleRequestCode">
      <div class="form-group">
        <label for="email">E-Mail:</label>
        <input type="email" id="email" v-model="email" required />
      </div>

      <button type="submit" :disabled="loading">
        {{ loading ? 'Wird gesendet...' : 'Bestätigungscode anfordern' }}
      </button>
    </form>

    <!-- Schritt 2: Eingabe des per E-Mail erhaltenen Codes -->
    <form v-else @submit.prevent="handleVerifyCode">
      <p>
        Wir haben einen Code an <strong>{{ email }}</strong> gesendet. Bitte gib ihn hier ein.
      </p>

      <div class="form-group">
        <label for="code">Bestätigungscode:</label>
        <input type="text" id="code" v-model="code" required />
      </div>

      <button type="submit" :disabled="loading">
        {{ loading ? 'Wird geprüft...' : 'Login abschließen' }}
      </button>
    </form>

    <pre v-if="errorMessage" class="error">{{ errorMessage }}</pre>
  </div>
</template>

<script setup>
import { ref } from 'vue';
import { useRouter } from 'vue-router';
import { requestPasswordResetCode, verifyPasswordResetCode } from '../services/api';

// Reaktive Zustandsvariablen für die Formular-Eingaben und den UI-Zustand.
const email = ref('');
const code = ref('');
const errorMessage = ref('');
const isCodeStep = ref(false); // Steuert, ob Schritt 1 (E-Mail) oder Schritt 2 (Code) angezeigt wird.
const loading = ref(false);

const router = useRouter();

/**
 * Eine einfache clientseitige Validierung für E-Mail-Adressen.
 * @param {string} value - Die zu prüfende E-Mail.
 * @returns {boolean}
 */
const isValidEmail = (value) => {
  const re =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(String(value).toLowerCase());
};

/**
 * Handler für Schritt 1: Fordert einen Passwort-Reset-Code vom Backend an.
 * Bei Erfolg wird die Ansicht auf den zweiten Schritt umgeschaltet.
 */
const handleRequestCode = async () => {
  errorMessage.value = '';
  loading.value = true;

  try {
    if (!isValidEmail(email.value)) {
      errorMessage.value = 'Bitte geben Sie eine gültige E-Mail-Adresse ein.';
      return;
    }

    await requestPasswordResetCode(email.value);
    isCodeStep.value = true;
  } catch (error) {
    errorMessage.value = error.message || 'Unbekannter Fehler';
  } finally {
    loading.value = false;
  }
};

/**
 * Handler für Schritt 2: Sendet den eingegebenen Code zur Verifizierung an das Backend.
 * Bei Erfolg wird der Benutzer eingeloggt und zur Profilseite weitergeleitet.
 */
const handleVerifyCode = async () => {
  errorMessage.value = '';
  loading.value = true;

  try {
    await verifyPasswordResetCode(email.value, code.value);
    router.push('/me');
  } catch (error) {
    errorMessage.value = error.message || 'Unbekannter Fehler';
  } finally {
    loading.value = false;
  }
};
</script>

<style lang="scss" scoped>
.form-wrapper {
  max-width: 400px;
  margin: 2rem auto;
  padding: 2rem;
  background: $card-background;
  border-radius: 8px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);

  h2 {
    margin-top: 0;
    color: $secondary-color;
    text-align: center;
    margin-bottom: 2rem;
  }

  p {
    text-align: center;
    line-height: 1.5;
  }

  .form-group {
    margin-bottom: 1.5rem;
    text-align: left;

    label {
      display: block;
      margin-bottom: 0.5rem;
      font-weight: bold;
      color: $text-color;
    }

    input {
      width: 100%;
      padding: 0.75rem;
      border: 1px solid $border-color;
      border-radius: 4px;
      font-size: 1rem;
      box-sizing: border-box;

      &:focus {
        outline: none;
        border-color: $primary-color;
        box-shadow: 0 0 0 3px rgba($primary-color, 0.2);
      }
    }
  }

  button {
    width: 100%;
    padding: 0.75rem;
    border: none;
    border-radius: 4px;
    background-color: $primary-color;
    color: white;
    font-size: 1rem;
    font-weight: bold;
    cursor: pointer;
    transition: background-color 0.2s;

    &:hover {
      background-color: $primary-color-hover;
    }

    &:disabled {
      opacity: 0.7;
      cursor: not-allowed;
    }
  }

  .error {
    color: red;
    margin-top: 1rem;
    text-align: center;
    white-space: pre-wrap;
    word-break: break-word;
  }
}
</style>